/**
 * 视频缓存工具
 * 使用 IndexedDB 存储视频文件，提升访问速度
 */

const DB_NAME = 'VideoCacheDB'
const DB_VERSION = 1
const STORE_NAME = 'videos'
const DEFAULT_MAX_CACHE_SIZE = 5 * 1024 * 1024 * 1024 // 默认最大缓存大小：5GB
const MAX_AGE = 3 * 24 * 60 * 60 * 1000 // 缓存有效期：3天
const DEFAULT_MAX_ITEMS = 1000 // 默认最大缓存条数：1000条

// 配置存储的 key
const CACHE_CONFIG_KEY = 'VIDEO_CACHE_CONFIG'

let db = null

/**
 * 获取缓存配置
 */
const getCacheConfig = () => {
  try {
    const configStr = localStorage.getItem(CACHE_CONFIG_KEY)
    if (configStr) {
      const config = JSON.parse(configStr)
      return {
        maxCacheSize: config.maxCacheSize || DEFAULT_MAX_CACHE_SIZE,
        maxItems: config.maxItems || DEFAULT_MAX_ITEMS
      }
    }
  } catch (error) {
    console.warn('读取缓存配置失败:', error)
  }
  return {
    maxCacheSize: DEFAULT_MAX_CACHE_SIZE,
    maxItems: DEFAULT_MAX_ITEMS
  }
}

/**
 * 设置缓存配置
 */
export const setCacheConfig = (config) => {
  try {
    const currentConfig = getCacheConfig()
    const newConfig = {
      maxCacheSize: config.maxCacheSize !== undefined ? config.maxCacheSize : currentConfig.maxCacheSize,
      maxItems: config.maxItems !== undefined ? config.maxItems : currentConfig.maxItems
    }
    localStorage.setItem(CACHE_CONFIG_KEY, JSON.stringify(newConfig))
    return true
  } catch (error) {
    console.warn('保存缓存配置失败:', error)
    return false
  }
}

/**
 * 获取当前缓存配置
 */
export const getCurrentCacheConfig = () => {
  return getCacheConfig()
}

/**
 * 打开数据库
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      reject(new Error('打开数据库失败'))
    }

    request.onsuccess = (event) => {
      db = event.target.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const database = event.target.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = database.createObjectStore(STORE_NAME, { keyPath: 'url' })
        objectStore.createIndex('timestamp', 'timestamp', { unique: false })
        objectStore.createIndex('size', 'size', { unique: false })
      }
    }
  })
}

/**
 * 获取缓存的视频 URL
 */
export const getCachedVideoUrl = async (videoUrl) => {
  if (!videoUrl) return null

  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(videoUrl)

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result
        if (!result) {
          resolve(null)
          return
        }

        // 检查是否过期
        const now = Date.now()
        if (now - result.timestamp > MAX_AGE) {
          // 已过期，删除缓存
          deleteCachedVideo(videoUrl)
          resolve(null)
          return
        }

        // 返回缓存的 blob URL
        if (result.blob) {
          const blobUrl = URL.createObjectURL(result.blob)
          resolve(blobUrl)
        } else {
          resolve(null)
        }
      }

      request.onerror = () => {
        resolve(null)
      }
    })
  } catch (error) {
    console.warn('获取缓存视频失败:', error)
    return null
  }
}

const hasCachedVideo = async (videoUrl) => {
  if (!videoUrl) return false

  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(videoUrl)

    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result
        if (!result) {
          resolve(false)
          return
        }

        const now = Date.now()
        if (now - result.timestamp > MAX_AGE) {
          deleteCachedVideo(videoUrl)
          resolve(false)
          return
        }

        resolve(Boolean(result.blob))
      }
      request.onerror = () => resolve(false)
    })
  } catch (error) {
    return false
  }
}

/**
 * 缓存视频
 */
export const cacheVideo = async (videoUrl) => {
  if (!videoUrl) return false

  try {
    // 先检查是否已缓存
    const cached = await hasCachedVideo(videoUrl)
    if (cached) {
      return true
    }

    // 下载视频（直接使用单请求下载，避免SSL证书问题）
    const { blob, size } = await downloadVideoSingle(videoUrl)

    // 检查缓存大小
    const config = getCacheConfig()
    const currentSize = await getCacheSize()
    if (currentSize + size > config.maxCacheSize) {
      // 清理旧缓存（按大小）
      await cleanOldCache(size)
    }

    // 检查缓存条数，如果超过最大条数，则删除最旧的数据
    await ensureMaxItemsLimit(config.maxItems)

    // 存储到 IndexedDB
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    const data = {
      url: videoUrl,
      blob: blob,
      timestamp: Date.now(),
      size: size
    }

    await new Promise((resolve, reject) => {
      const request = store.put(data)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('存储失败'))
    })

    return true
  } catch (error) {
    // 对于 403/401 权限错误，静默失败，不输出警告（这些错误是预期的，不需要警告用户）
    if (error.message && (error.message.includes('403') || error.message.includes('401'))) {
      // 静默失败，不输出日志
      return false
    }
    // 其他错误才输出警告
    console.warn('缓存视频失败:', error)
    return false
  }
}

/**
 * 单请求下载视频
 */
const downloadVideoSingle = async (videoUrl) => {
  const response = await fetch(videoUrl, { referrerPolicy: 'no-referrer' })
  if (!response.ok) {
    throw new Error(`下载失败: ${response.status}`)
  }
  const blob = await response.blob()
  return { blob, size: blob.size }
}


/**
 * 获取缓存大小
 */
const getCacheSize = async () => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const index = store.index('size')
    const request = index.openCursor()
    let totalSize = 0

    return new Promise((resolve) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          totalSize += cursor.value.size || 0
          cursor.continue()
        } else {
          resolve(totalSize)
        }
      }

      request.onerror = () => {
        resolve(0)
      }
    })
  } catch (error) {
    return 0
  }
}

/**
 * 清理旧缓存
 */
const cleanOldCache = async (needSpace) => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    const index = store.index('timestamp')
    const request = index.openCursor()
    let freedSpace = 0

    return new Promise((resolve) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor && freedSpace < needSpace) {
          const data = cursor.value
          freedSpace += data.size || 0
          cursor.delete()
          cursor.continue()
        } else {
          resolve()
        }
      }

      request.onerror = () => {
        resolve()
      }
    })
  } catch (error) {
    console.warn('清理缓存失败:', error)
  }
}

/**
 * 删除缓存的视频
 */
export const deleteCachedVideo = async (videoUrl) => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    await new Promise((resolve, reject) => {
      const request = store.delete(videoUrl)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('删除失败'))
    })
  } catch (error) {
    console.warn('删除缓存失败:', error)
  }
}

/**
 * 获取或缓存视频 URL（优先使用缓存）
 */
export const getOrCacheVideoUrl = async (videoUrl) => {
  if (!videoUrl) return null

  // 先尝试从缓存获取
  const cachedUrl = await getCachedVideoUrl(videoUrl)
  if (cachedUrl) {
    // 后台更新缓存（如果过期）
    cacheVideo(videoUrl).catch(() => {})
    return cachedUrl
  }

  // 缓存不存在，异步缓存（不阻塞）
  cacheVideo(videoUrl).catch(() => {})

  // 返回原始 URL
  return videoUrl
}

/**
 * 预加载视频（后台缓存）
 */
export const preloadVideo = async (videoUrl) => {
  if (!videoUrl) return
  // 异步缓存，不阻塞
  cacheVideo(videoUrl).catch(() => {})
}

/**
 * 清理所有缓存
 */
export const clearAllCache = async () => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    await new Promise((resolve, reject) => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('清理失败'))
    })
  } catch (error) {
    console.warn('清理所有缓存失败:', error)
  }
}

/**
 * 清理缓存，只保留最新的指定条数
 * @param {number} keepCount - 保留的条数，默认100
 */
export const cleanCacheKeepLatest = async (keepCount = 100) => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    // 先获取当前总条数
    const countRequest = store.count()
    const totalCount = await new Promise((resolve, reject) => {
      countRequest.onsuccess = () => resolve(countRequest.result || 0)
      countRequest.onerror = () => reject(new Error('获取缓存条数失败'))
    })

    if (totalCount <= keepCount) {
      return { deleted: 0, kept: totalCount }
    }

    // 获取所有记录并按时间戳排序（从新到旧）
    const allRecords = []
    const index = store.index('timestamp')
    const cursorRequest = index.openCursor(null, 'prev') // 从新到旧

    await new Promise((resolve) => {
      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          allRecords.push({
            url: cursor.value.url,
            timestamp: cursor.value.timestamp
          })
          cursor.continue()
        } else {
          resolve()
        }
      }
      cursorRequest.onerror = () => resolve()
    })

    // 保留最新的 keepCount 条，删除其余的
    const toDelete = allRecords.slice(keepCount)
    let deleted = 0

    // 在同一事务中删除所有需要删除的记录
    const deletePromises = toDelete.map((record) => {
      return new Promise((resolve) => {
        const deleteRequest = store.delete(record.url)
        deleteRequest.onsuccess = () => {
          deleted++
          resolve()
        }
        deleteRequest.onerror = () => {
          console.warn(`删除缓存项失败: ${record.url}`)
          resolve() // 继续删除其他项
        }
      })
    })

    await Promise.all(deletePromises)

    return { deleted, kept: keepCount }
  } catch (error) {
    console.warn('清理缓存失败:', error)
    throw error
  }
}

/**
 * 获取缓存统计信息
 */
export const getCacheStats = async () => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.count()
    const config = getCacheConfig()
    
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const count = request.result
        getCacheSize().then((size) => {
          resolve({
            count,
            size,
            maxSize: config.maxCacheSize,
            maxItems: config.maxItems
          })
        })
      }
      request.onerror = () => {
        resolve({ count: 0, size: 0, maxSize: config.maxCacheSize, maxItems: config.maxItems })
      }
    })
  } catch (error) {
    const config = getCacheConfig()
    return { count: 0, size: 0, maxSize: config.maxCacheSize, maxItems: config.maxItems }
  }
}

/**
 * 确保缓存条数不超过最大限制
 * 如果条数超过 maxItems，则按时间从旧到新删除多余记录
 */
const ensureMaxItemsLimit = async (maxItems) => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    // 先获取当前总条数
    const countRequest = store.count()

    const totalCount = await new Promise((resolve, reject) => {
      countRequest.onsuccess = () => resolve(countRequest.result || 0)
      countRequest.onerror = () => reject(new Error('获取缓存条数失败'))
    })

    if (totalCount < maxItems) {
      return
    }

    const index = store.index('timestamp')
    const cursorRequest = index.openCursor()
    let deleted = 0
    const needDelete = totalCount - maxItems + 1 // 为新记录腾出一个位置

    await new Promise((resolve) => {
      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor && deleted < needDelete) {
          cursor.delete()
          deleted += 1
          cursor.continue()
        } else {
          resolve()
        }
      }
      cursorRequest.onerror = () => resolve()
    })
  } catch (error) {
    console.warn('限制缓存条数失败:', error)
  }
}
